import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Notification } from "../common/style";
import {
  deleteUserProject,
  getBomSummary,
  refreshPrice,
  refreshDeliveryDate,
  updateUserProject,
  refreshTemplates as refreshTemplatesService,
} from "./service";
import { AppContext } from "../context";
import { ModalButton, TableViewToggler } from "../project/style";
import ConfirmationModal from "../project/ConfirmationModal";
import DeliveryInfoPopover from "./components/DeliveryInfoPopover";
import { formatDateWithSuffix } from "../common/DateHelpers";
import { isMobileOnly } from "react-device-detect";
import { isInternalAccess } from "../common/service";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap";
import OrderInfo from "./OrderInfo";

const ProjectHeader = ({
  projectId,
  showLegacyMaterials,
  setCurrentTab,
  isAdmin,
  access,
  isOrderDraft,
  projectSummary,
  setProjectSummary,
  reloadRoom,
  version,
  setVersion,
  isCurrentTab,
  isListProject,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [isDeliveryDateLoading, setIsDeliveryDateLoading] = useState(false);
  const [templatesRefreshing, setTemplatesRefreshing] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(true);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const { bomSummary, setBomSummary } = useContext(AppContext);
  const navigate = useNavigate();
  const [openRenameProject, setOpenRenameProject] = useState(false);
  const [projectName, setProjectName] = useState(projectSummary.name);
  const [openArchiveProjectModal, setOpenArchiveProjectModal] = useState(false);
  const [openRefreshPriceModal, setOpenRefreshPriceModal] = useState(false);
  const [isDeliveryInfoOpen, setIsDeliveryInfoOpen] = useState(false);
  const deliveryDateRef = useRef(null);

  const toggleRenameProject = () => {
    if (!openRenameProject) {
      setProjectName(projectSummary.name);
    }

    setOpenRenameProject(!openRenameProject);
  };

  const toggleArchiveProjectModal = () =>
    setOpenArchiveProjectModal(!openArchiveProjectModal);

  const toggleRefreshPriceModal = () =>
    setOpenRefreshPriceModal(!openRefreshPriceModal);

  const fetchSummary = useCallback(
    (refresh = false) => {
      return getBomSummary(projectId, refresh)
        .then((response) => {
          setBomSummary({ ...response, loaded: true });
        })
        .catch((error) => {
          setNotification({ message: error.error, type: "error" });
          setIsNotificationVisible(true);
        });
    },
    [projectId, setBomSummary, setNotification, setIsNotificationVisible]
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const changeProjectName = (name) => {
    updateUserProject({ projectId, payload: { name } })
      .then(() => {
        setProjectSummary({ ...projectSummary, name: name });
        toggleRenameProject();
      })
      .catch((error) => {
        setNotification({ message: error.error, type: "error" });
        setIsNotificationVisible(true);
      });
  };

  const deleteProject = () => {
    deleteUserProject(projectId)
      .then(() => {
        toggleArchiveProjectModal();
        navigate("../../projects");
      })
      .catch((error) => {
        setNotification({ message: error.error, type: "error" });
        setIsNotificationVisible(true);
      });
  };

  const recalculatePrice = (force = false) => {
    // For refresh if admin (to used updated laminate prices)
    return refreshPrice(projectId, force)
      .then(() => {
        reloadRoom();
      })
      .catch((error) => {
        setNotification({ message: error.error, type: "error" });
        setIsNotificationVisible(true);
      });
  };

  const refreshTemplates = () => {
    setTemplatesRefreshing(true);

    return refreshTemplatesService(projectId)
      .then((response) => {
        reloadRoom(true).then(() => {
          setProjectSummary(response);
        });
      })
      .catch((error) => {
        setNotification({ message: error.error, type: "error" });
        setIsNotificationVisible(true);
      })
      .finally(() => setTemplatesRefreshing(false));
  };

  const handleDeliveryButtonClick = () => {
    setIsDeliveryDateLoading(true);

    return refreshDeliveryDate(projectId)
      .then((response) => {
        setProjectSummary(response);
      })
      .catch((error) => {
        setNotification({ message: error.error, type: "error" });
        setIsNotificationVisible(true);
      })
      .finally(() => setIsDeliveryDateLoading(false));
  };

  if (!bomSummary) return;

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}

      <div
        className="project-header d-flex align-items-center border-bottom"
        style={{
          background: "var(--color-dark-1)",
          color: "#FFF",
          boxShadow: "0 0 4px 0px #CCC",
          position: "sticky",
          top: 0,
          zIndex: 1000,
        }}
      >
        <div className="d-flex flex-grow-1 align-items-center project-name ps-2">
          <i
            onClick={() => navigate("/projects")}
            className="fa-solid fa-arrow-left fs-5 pointer"
          />
          <div
            className={`text-capitalize fw-bold ms-2 ${
              isMobileOnly ? "fs-12px text-nowrap text-truncate" : "fs-6"
            }`}
            style={{ width: isMobileOnly ? "150px" : "" }}
          >
            {projectSummary.name}
          </div>

          {isListProject && !isMobileOnly && (
            <>
              <TableViewToggler
                className={`${version === 1 ? "active" : ""} ms-2`}
                onClick={() => {
                  version === 1 ? setVersion(2) : setVersion(1);
                }}
              >
                Room View
              </TableViewToggler>
              {isInternalAccess() && projectSummary.owner.org_name && (
                <div className="text-white ms-2 small">
                  {projectSummary.owner.org_name}
                </div>
              )}
            </>
          )}
        </div>
        {!isMobileOnly && (
          <OrderInfo
            projectSummary={projectSummary}
            setProjectSummary={setProjectSummary}
            setCurrentTab={setCurrentTab}
            reloadRoom={reloadRoom}
          />
        )}
        <div className="d-flex h-100">
          {templatesRefreshing ? (
            <div className="status me-2 ms-3 align-content-center">
              <Spinner size="sm fs-4"></Spinner> &nbsp;
            </div>
          ) : (
            (projectSummary.is_template_stale ||
              projectSummary.is_price_stale) && (
              <>
                <div className="align-content-center ms-3 me-2">
                  <i
                    id="refresh_templates"
                    className="fa-solid fa-rotate pointer fs-4 "
                    onClick={() => refreshTemplates()}
                  ></i>
                  <UncontrolledTooltip
                    target="refresh_templates"
                    placement="bottom"
                  >
                    Refresh Preview
                  </UncontrolledTooltip>
                </div>
              </>
            )
          )}
          {!isMobileOnly && (
            <div
              className={`d-flex align-items-center fs-6 px-2 position-relative border-start border-secondary ms-2 ${
                !projectSummary.is_delivery_date_stale &&
                !projectSummary.delivery_date_frozen &&
                "pointer"
              }`}
            >
              {!projectSummary.is_template_stale &&
              !projectSummary.is_price_stale &&
              projectSummary.is_delivery_date_stale ? (
                <button
                  className="btn btn-sm btn-outline-light ms-2"
                  onClick={handleDeliveryButtonClick}
                  disabled={isDeliveryDateLoading}
                  style={{ fontSize: "0.8rem", padding: "2px 8px" }}
                >
                  {isDeliveryDateLoading ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Refreshing...
                    </>
                  ) : (
                    "Refresh Delivery Date"
                  )}
                </button>
              ) : (
                access.view_price_detail && (
                  <>
                    <div
                      ref={deliveryDateRef}
                      className={`d-flex h-100 align-items-center ${
                        projectSummary.is_delivery_date_stale
                          ? "text-secondary"
                          : "text-white"
                      } ${
                        isMobileOnly
                          ? "flex-column justify-content-center text-center"
                          : ""
                      }`}
                      style={{ fontSize: "0.9rem" }}
                      onMouseEnter={() => setIsDeliveryInfoOpen(true)}
                      onMouseLeave={() => setIsDeliveryInfoOpen(false)}
                    >
                      <i
                        className={`fa-solid fa-truck ${
                          isMobileOnly ? "mb-1" : "me-2"
                        }`}
                      />
                      {projectSummary &&
                      projectSummary.delivery_date_from &&
                      projectSummary.delivery_date_to ? (
                        <span
                          className={`${
                            isMobileOnly ? "fs-10px text-nowrap" : "fs-14px"
                          }`}
                        >
                          {projectSummary.delivery_date_from ===
                          projectSummary.delivery_date_to ? (
                            formatDateWithSuffix(
                              projectSummary.delivery_date_from
                            )
                          ) : (
                            <>
                              {formatDateWithSuffix(
                                projectSummary.delivery_date_from
                              )}
                              <i className="fa-solid fa-plus fs-12px text-AAAAAA mx-1"></i>
                              {formatDateWithSuffix(
                                projectSummary.delivery_date_to
                              )}
                            </>
                          )}
                        </span>
                      ) : projectSummary &&
                        projectSummary.planned_delivery_date ? (
                        <span
                          className={`${
                            isMobileOnly ? "fs-10px text-nowrap" : ""
                          }`}
                        >
                          {formatDateWithSuffix(
                            projectSummary.planned_delivery_date
                          )}
                        </span>
                      ) : (
                        <span
                          className={`text-secondary ${
                            isMobileOnly ? "fs-10px text-nowrap" : "small"
                          }`}
                        >
                          Not available
                        </span>
                      )}
                    </div>
                    {!projectSummary.is_delivery_date_stale &&
                      !projectSummary.delivery_date_frozen && (
                        <DeliveryInfoPopover
                          projectSummary={projectSummary}
                          isDeliveryInfoOpen={isDeliveryInfoOpen}
                          targetRef={deliveryDateRef}
                        />
                      )}
                  </>
                )
              )}
            </div>
          )}

          {access.view_price && projectSummary && (
            <div
              className={`price_summary border-start border-secondary d-flex align-items-center pointer ${
                isMobileOnly ? "fs-10px text-nowrap" : "px-2 fs-6"
              }`}
            >
              {/*{isLoading ? (*/}
              {/*  <div className="status me-2">*/}
              {/*    <Spinner size="sm"></Spinner> &nbsp;*/}
              {/*  </div>*/}
              {/*) : (*/}
              {/*  projectSummary.is_price_stale &&*/}
              {/*  !projectSummary.is_template_stale &&*/}
              {/*  access.refresh_price && (*/}
              {/*    <div className="status me-2">*/}
              {/*      <i*/}
              {/*        id="refresh_price"*/}
              {/*        className="fa-solid fa-rotate pointer fs-5"*/}
              {/*        title="Refresh price"*/}
              {/*        onClick={() => recalculatePrice()}*/}
              {/*      ></i>*/}
              {/*      <UncontrolledTooltip*/}
              {/*        target="refresh_price"*/}
              {/*        placement="bottom"*/}
              {/*      >*/}
              {/*        Refresh Price*/}
              {/*      </UncontrolledTooltip>*/}
              {/*    </div>*/}
              {/*  )*/}
              {/*)}*/}
              <div className="value p-0">
                {projectSummary &&
                projectSummary.price_summary &&
                projectSummary.price_summary.base_price !== undefined &&
                projectSummary.price_summary.base_price !== 0 ? (
                  <div
                    className={`amount d-flex align-items-center ${
                      isMobileOnly ? "fs-4 text-nowrap me-1" : "fs-5"
                    }`}
                    id="totalPrice"
                    onClick={() => setCurrentTab("order")}
                  >
                    {!projectSummary.laminates_approved && (
                      <span className={"fs-12px me-12px yellow-color"}>
                        Price subject to approval
                        <i className={"ms-1 fa-solid fa-circle-info"}></i>
                      </span>
                    )}

                    <div
                      className={`d-flex flex-column align-items-center ${
                        isMobileOnly ? "" : ""
                      }`}
                    >
                      <span
                        className={`fw-bold mt-2 fs-5 ${
                          projectSummary.is_price_stale
                            ? "text-secondary"
                            : "text-white"
                        }`}
                      >
                        ₹{" "}
                        {(
                          (projectSummary.price_summary.base_price || 0) +
                          (projectSummary.price_summary.total_upgrades || 0) +
                          (projectSummary.price_summary.total_addons || 0) +
                          (projectSummary.price_summary.total_material_items || 0)
                        ).toLocaleString("en-IN")}
                      </span>
                      <span className="grey_letter fs-12px text-center mb-2">
                        incl. GST
                      </span>
                    </div>

                    {projectSummary.is_price_stale && (
                      <UncontrolledTooltip
                        target="totalPrice"
                        placement="bottom"
                      >
                        Price outdated
                      </UncontrolledTooltip>
                    )}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
          )}
          {access.view_price && access.view_price_detail && projectSummary && (
            <div
              className={`price_summary border-start border-secondary d-flex align-items-center pointer ${
                isMobileOnly ? "fs-10px text-nowrap" : "px-2 fs-6"
              }`}
              onClick={() => setCurrentTab("order")}
            >
              <div className="value p-0">
                {projectSummary &&
                projectSummary.price_summary &&
                projectSummary.price_summary.base_price !== undefined &&
                projectSummary.price_summary.base_price !== 0 ? (
                  <div
                    className={`amount d-flex flex-column ${
                      isMobileOnly ? "fs-4 text-nowrap me-1" : "fs-5"
                    }`}
                    style={{ width: "100%" }}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <span
                        className="grey_letter fs-10px me-2"
                        style={{ lineHeight: "1.2" }}
                      >
                        base
                      </span>
                      <span
                        className={`fs-13px ${
                          projectSummary.is_price_stale
                            ? "text-secondary"
                            : "text-white"
                        }`}
                      >
                        ₹{" "}
                        {(
                          projectSummary.price_summary.base_price || 0
                        ).toLocaleString("en-IN")}
                      </span>
                    </div>

                    {(projectSummary.price_summary.total_upgrades !==
                      undefined ||
                      projectSummary.price_summary.total_addons !==
                        undefined ||
                      projectSummary.price_summary.total_material_items !==
                        undefined) &&
                      (projectSummary.price_summary.total_upgrades || 0) +
                        (projectSummary.price_summary.total_addons || 0) +
                        (projectSummary.price_summary.total_material_items || 0) >
                        0 && <hr className="m-0 my-1 text-white" />}

                    {(projectSummary.price_summary.total_upgrades !==
                      undefined ||
                      projectSummary.price_summary.total_addons !==
                        undefined ||
                      projectSummary.price_summary.total_material_items !==
                        undefined) &&
                      (projectSummary.price_summary.total_upgrades || 0) +
                        (projectSummary.price_summary.total_addons || 0) +
                        (projectSummary.price_summary.total_material_items || 0) >
                        0 && (
                        <div
                          className="d-flex align-items-center justify-content-between w-100"
                          id="totalUpgradesAddons"
                        >
                          <span
                            className="grey_letter fs-10px me-2"
                            style={{ lineHeight: "1.2" }}
                          >
                            upgrades
                          </span>
                          <span
                            className={`fs-13px ${
                              projectSummary.is_price_stale
                                ? "text-secondary"
                                : "text-white"
                            }`}
                          >
                            ₹{" "}
                            {(
                              (projectSummary.price_summary.total_upgrades ||
                                0) +
                              (projectSummary.price_summary.total_addons || 0) +
                              (projectSummary.price_summary.total_material_items || 0)
                            ).toLocaleString("en-IN")}
                          </span>
                          {projectSummary.is_price_stale && (
                            <UncontrolledTooltip
                              target="totalUpgradesAddons"
                              placement="bottom"
                            >
                              Price outdated
                            </UncontrolledTooltip>
                          )}
                        </div>
                      )}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
          )}
        </div>
        {version === 2 && !isMobileOnly && (
          <div className="d-flex align-items-center ps-3 column-gap-3 h-100 border-start border-secondary">
            <i
              className={`fa-solid fa-palette fs-5 pointer ${(isCurrentTab("materials"))? "text-white":""
                }`}
              onClick={() => setCurrentTab("materials")}
              title="Materials"
            ></i>
            <i
              className={`fa-solid fa-gear fs-5 pointer ${
                isCurrentTab("settings") ? "text-white" : ""
              }`}
              onClick={() => setCurrentTab("settings")}
              title="Settings"
            ></i>
            <i
              className={`fa-solid fa-camera fs-5 pointer ${isCurrentTab("renders")? "text-white":""
                }`}
              onClick={() => {
                setCurrentTab("renders");
              }}
              title="Installation Drawings"
            ></i>
            <i
              className={`fa-solid fa-shopping-cart fs-5 pointer ${
                isCurrentTab("order") ? "text-white" : ""
              }`}
              onClick={() => setCurrentTab("order")}
              title="Order"
            ></i>
          </div>
        )}

        <UncontrolledDropdown
          className={`${isMobileOnly ? "ms-0 ps-0" : "ms-3"}`}
          style={isMobileOnly ? { width: "30px" } : {}}
        >
          <DropdownToggle
            className="border-0 order-download pointer"
            style={isMobileOnly ? { padding: "6px" } : {}}
          >
            {isMobileOnly ? (
              <i className="fa-solid fa-ellipsis-vertical fs-4 p-2" />
            ) : (
              <i className="fa-solid fa-ellipsis fs-6" />
            )}
          </DropdownToggle>
          <DropdownMenu className="py-1">
            <DropdownItem
              onClick={() =>
                !projectSummary.is_template_stale && navigate(`assembly`)
              }
              className={`${
                !projectSummary.is_template_stale ? "" : "disabled"
              }`}
              id="assembly"
            >
              Assembly
            </DropdownItem>
            {projectSummary.is_template_stale && (
              <UncontrolledTooltip
                style={{
                  padding: "6px 20px",
                }}
                target="assembly"
                placement="left"
              >
                Refresh Preview to enable this{" "}
              </UncontrolledTooltip>
            )}
            <>
              <DropdownItem
                id="archive_project"
                onClick={access.can_edit_project && toggleArchiveProjectModal}
                className={`${access.can_edit_project ? "" : "disabled"}`}
              >
                Archive
              </DropdownItem>
              {!isOrderDraft && (
                <UncontrolledTooltip
                  style={{
                    padding: "6px 20px",
                  }}
                  target="archive_project"
                  placement="left"
                >
                  Allowed only in draft
                </UncontrolledTooltip>
              )}
            </>
            <>
              <DropdownItem
                id="rename_project"
                className={`${access.can_edit_project ? "" : "disabled"}`}
                onClick={() => {
                  if (access.can_edit_project) {
                    toggleRenameProject();
                  }
                }}
              >
                Rename
              </DropdownItem>
              {!isMobileOnly && (
                <>
                  <DropdownItem
                    id="refresh_price"
                    onClick={() => {
                      if (access.refresh_price) {
                        recalculatePrice(true);
                      }
                    }}
                    disabled={!access.refresh_price}
                  >
                    Refresh Price
                  </DropdownItem>
                  {!isOrderDraft && (
                    <UncontrolledTooltip
                      style={{
                        padding: "6px 20px",
                      }}
                      target="refresh_price"
                      placement="left"
                    >
                      Allowed only in draft
                    </UncontrolledTooltip>
                  )}
                </>
              )}
              {!isOrderDraft && (
                <UncontrolledTooltip
                  style={{
                    padding: "6px 20px",
                  }}
                  target="rename_project"
                  placement="left"
                >
                  Allowed only in draft
                </UncontrolledTooltip>
              )}
              {showLegacyMaterials && (
                <DropdownItem
                  onClick={() => {
                    navigate("materials");
                    setCurrentTab("materials");
                  }}
                >
                  Orders
                </DropdownItem>
              )}
              <DropdownItem onClick={() => setCurrentTab("price_change_logs")}>
                Price Change Log
              </DropdownItem>
            </>
            {isAdmin && (
              <>
                <hr className="m-0 opacity-100" style={{ color: "#4F5F71" }} />
                <DropdownItem
                  id="production"
                  onClick={() =>
                    access.bom_access &&
                    navigate(`orders/${projectSummary.order_id}`)
                  }
                  className={`${access.bom_access ? "" : "disabled"}`}
                >
                  Production
                </DropdownItem>
                {!access.bom_access && (
                  <UncontrolledTooltip
                    style={{
                      padding: "6px 20px",
                    }}
                    target="production"
                    placement="left"
                  >
                    Allowed only after finalised
                  </UncontrolledTooltip>
                )}
                <DropdownItem onClick={() => navigate(`add_ons`)}>
                  Add-Ons
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>
      {isMobileOnly && (
        <div
          className="project-header d-flex justify-content-between align-items-center border-bottom p-2"
          style={{ backgroundColor: "#516276" }}
        >
          <div className="d-flex align-items-center gap-2">
            {projectSummary.order_status && (
              <div
                className="text-capitalize ms-2 pointer rounded-1 px-2"
                style={{
                  paddingTop: "0px !important",
                  paddingBottom: "0px !important",
                  color: "#666666",
                  backgroundColor: "#D9D9D9",
                }}
              >
                {projectSummary.order_status}
              </div>
            )}
            <OrderInfo
              projectSummary={projectSummary}
              setProjectSummary={setProjectSummary}
              setCurrentTab={setCurrentTab}
              reloadRoom={reloadRoom}
              isMobile={true}
            />
          </div>
          <div
            className={`d-flex align-items-center fs-6 px-2 position-relative ${
              !projectSummary.is_delivery_date_stale &&
              !projectSummary.delivery_date_frozen &&
              "pointer"
            }`}
          >
            {isDeliveryDateLoading ? (
              <div className="status me-2">
                <Spinner size="sm"></Spinner> &nbsp;
              </div>
            ) : (
              projectSummary.is_delivery_date_stale &&
              access.refresh_price && (
                <div className="me-2">
                  <i
                    id="recalculate_delivery_date"
                    className="fa-solid fa-rotate pointer text-white"
                    onClick={() => refreshDeliveryDate()}
                  ></i>
                  <UncontrolledTooltip
                    target="recalculate_delivery_date"
                    placement="bottom"
                  >
                    Refresh Delivery Date
                  </UncontrolledTooltip>
                </div>
              )
            )}
            <>
              <div
                ref={deliveryDateRef}
                className={`d-flex h-100 align-items-center px-2 text-white ${
                  projectSummary.is_delivery_date_stale
                    ? "text-white"
                    : "text-secondary"
                } ${
                  isMobileOnly
                    ? "flex-column justify-content-center text-center"
                    : ""
                }`}
                style={{ fontSize: "0.9rem" }}
                onMouseEnter={() => setIsDeliveryInfoOpen(true)}
                onMouseLeave={() => setIsDeliveryInfoOpen(false)}
              >
                <div>
                  <i
                    className={`fa-solid fa-truck ${
                      isMobileOnly ? "mb-1 me-2" : "me-2"
                    }`}
                  />
                  <small>Delivery Date</small>
                </div>
                {projectSummary && projectSummary.planned_delivery_date ? (
                  <span
                    className={`${isMobileOnly ? "fs-10px text-nowrap" : ""}`}
                  >
                    {formatDateWithSuffix(projectSummary.planned_delivery_date)}
                  </span>
                ) : (
                  <span
                    className={`text-secondary ${
                      isMobileOnly ? "fs-10px text-nowrap" : "small"
                    }`}
                  >
                    Not available
                  </span>
                )}
              </div>
              {!projectSummary.is_delivery_date_stale &&
                !projectSummary.delivery_date_frozen && (
                  <DeliveryInfoPopover
                    projectSummary={projectSummary}
                    isDeliveryInfoOpen={isDeliveryInfoOpen}
                    targetRef={deliveryDateRef}
                  />
                )}
            </>
            <button
              className="btn btn-sm btn-outline-light ms-2"
              onClick={handleDeliveryButtonClick}
              disabled={
                projectSummary.is_template_stale ||
                projectSummary.is_price_stale
              }
              style={{ fontSize: "0.8rem", padding: "2px 8px" }}
            >
              Refresh Delivery Date
            </button>
          </div>
        </div>
      )}
      <Modal
        isOpen={openRenameProject}
        toggle={toggleRenameProject}
        backdrop="static"
      >
        <ModalHeader>Rename Project</ModalHeader>
        <ModalBody>
          <Input
            type="text"
            className="mt-1"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
        </ModalBody>
        <ModalFooter className="pt-0 border-0">
          <ModalButton
            className="mt-0"
            color="primary"
            onClick={() => changeProjectName(projectName)}
            disabled={!projectSummary.name.trim()}
          >
            Save
          </ModalButton>
          <ModalButton
            className="mt-0"
            color="secondary"
            onClick={toggleRenameProject}
          >
            Cancel
          </ModalButton>
        </ModalFooter>
      </Modal>

      <ConfirmationModal
        isOpen={openArchiveProjectModal}
        toggle={toggleArchiveProjectModal}
        onConfirm={deleteProject}
        message={"Are you sure you want to archive this project?"}
        headingText={"Archive Project"}
      />

      <ConfirmationModal
        isOpen={openRefreshPriceModal}
        toggle={toggleRefreshPriceModal}
        onConfirm={recalculatePrice}
        message="Are you sure you want to recompute the modules price?"
        headingText="Recompute Price"
      />
    </>
  );
};

export default ProjectHeader;
